import { action, computed, makeObservable, observable, reaction } from 'mobx'

import { DisposeBag } from '@src/lib/DisposeBag/DisposeBag'
import UserIdleDetector, { type UserState } from '@src/lib/idle/UserIdleDetector'
import type Service from '@src/service'

export default class PresenceService {
  protected readonly idleDetector = new UserIdleDetector()
  protected readonly disposeBag = new DisposeBag()

  _visibilityState: DocumentVisibilityState = 'visible'

  _windowFocused = document.hasFocus()

  constructor(private readonly root: Service) {
    makeObservable<this, 'handleVisibilityChange' | 'handleWindowFocusChange'>(this, {
      _visibilityState: observable.ref,
      _windowFocused: observable.ref,
      activityState: computed,
      visibilityState: computed,
      isForeground: computed,
      handleVisibilityChange: action.bound,
      handleWindowFocusChange: action.bound,
    })

    this.disposeBag.add(
      reaction(
        () => this.root.flags.flags.userIdleTimeout,
        (threshold) => {
          this.idleDetector.stop()

          if (typeof threshold === 'number' && threshold > 0) {
            this.idleDetector.start({ threshold })
          }
        },
      ),
    )

    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    window.addEventListener('focus', this.handleWindowFocusChange)
    window.addEventListener('blur', this.handleWindowFocusChange)

    this.disposeBag.add(() => {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange)
      window.removeEventListener('focus', this.handleWindowFocusChange)
      window.removeEventListener('blur', this.handleWindowFocusChange)
    })
  }

  get activityState(): UserState {
    return this.idleDetector.userState
  }

  get visibilityState(): DocumentVisibilityState {
    return this._visibilityState
  }

  /**
   * Whether the user is in Quo: the page is visible and its window has focus.
   * Switching browser tab hides the page but can leave the window focused;
   * switching app, or to another window such as a call overlay, takes focus
   * but leaves the page visible.
   */
  get isForeground(): boolean {
    return this._visibilityState === 'visible' && this._windowFocused
  }

  protected handleVisibilityChange() {
    this._visibilityState = document.visibilityState
  }

  protected handleWindowFocusChange() {
    this._windowFocused = document.hasFocus()
  }

  tearDown(): void {
    this.disposeBag.dispose()
    this.idleDetector.stop()
  }
}
